import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useSupplierCrmOrgStore } from '../../../store/supplierCrmOrg.store';
import { automationsService, type Automation, type AutomationInput, type AutomationLog } from '../../../services/projectManagement/automationsService';
import { pmMembersService, getDisplayName, type ProjectManagementMember } from '../../../services/projectManagement/membersService';

const AutomationsTab: React.FC = () => {
  const { activeOrgId } = useSupplierCrmOrgStore();
  const [loading, setLoading] = useState(true);
  const [automations, setAutomations] = useState<Automation[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [logs, setLogs] = useState<AutomationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedAutomationId, setSelectedAutomationId] = useState<string>('all');

  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [createEnabled, setCreateEnabled] = useState(true);
  const [createTriggerType, setCreateTriggerType] = useState<string>('task_created');
  const [createActionType, setCreateActionType] = useState<string>('add_comment');
  const [actionMessage, setActionMessage] = useState('');
  const [actionStatus, setActionStatus] = useState<string>('todo');
  const [actionAssigneeId, setActionAssigneeId] = useState<string>('');
  const [actionField, setActionField] = useState('');
  const [actionValue, setActionValue] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [members, setMembers] = useState<ProjectManagementMember[]>([]);

  const fetchAutomations = useCallback(async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const { data, error } = await automationsService.list({ organization_id: activeOrgId });
      if (error) {
        toast.error('Failed to load automations');
        return;
      }
      setAutomations(data || []);
    } catch (error) {
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    void fetchAutomations();
  }, [fetchAutomations]);

  const fetchLogs = useCallback(async () => {
    if (!activeOrgId) return;
    setLogsLoading(true);
    try {
      const { data, error } = await automationsService.getLogs({
        organization_id: activeOrgId,
        automation_id: selectedAutomationId !== 'all' ? selectedAutomationId : undefined,
        limit: 50,
      });
      if (error) {
        setLogs([]);
      } else {
        setLogs(data || []);
      }
    } catch {
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [activeOrgId, selectedAutomationId]);

  useEffect(() => {
    void fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (!showCreateModal) return;
    setCreateName('');
    setCreateDescription('');
    setCreateEnabled(true);
    setCreateTriggerType('task_created');
    setCreateActionType('add_comment');
    setActionMessage('');
    setActionStatus('todo');
    setActionAssigneeId('');
    setActionField('');
    setActionValue('');

    if (!activeOrgId) {
      setMembers([]);
      return;
    }

    void (async () => {
      const { data, error } = await pmMembersService.list(activeOrgId);
      if (error) {
        setMembers([]);
      } else {
        setMembers(data || []);
      }
    })();
  }, [showCreateModal, activeOrgId]);

  const handleToggle = async (automation: Automation) => {
    if (!activeOrgId) return;
    
    const { error } = await automationsService.toggle(
      automation.id,
      activeOrgId,
      !automation.enabled
    );
    
    if (error) {
      toast.error('Failed to toggle automation');
    } else {
      toast.success(`Automation ${!automation.enabled ? 'enabled' : 'disabled'}`);
      void fetchAutomations();
    }
  };

  const handleCreate = async () => {
    if (!activeOrgId) return;

    const name = createName.trim();
    if (!name) {
      toast.error('Name is required');
      return;
    }

    const actionType = String(createActionType || '').trim();
    if (!actionType) {
      toast.error('Action is required');
      return;
    }

    const triggerType = String(createTriggerType || '').trim();
    if (!triggerType) {
      toast.error('Trigger is required');
      return;
    }

    const params: Record<string, any> = {};
    if (actionType === 'add_comment') {
      const msg = actionMessage.trim();
      if (!msg) {
        toast.error('Comment message is required');
        return;
      }
      params.message = msg;
    }

    if (actionType === 'set_task_status') {
      const st = String(actionStatus || '').trim();
      if (!st) {
        toast.error('Status is required');
        return;
      }
      params.status = st;
    }

    if (actionType === 'assign_task') {
      const aid = String(actionAssigneeId || '').trim();
      if (!aid) {
        toast.error('Assignee is required');
        return;
      }
      params.assignee_id = aid;
    }

    if (actionType === 'update_field') {
      const f = actionField.trim();
      if (!f) {
        toast.error('Field is required');
        return;
      }
      params.field = f;
      params.value = actionValue;
    }

    const input: AutomationInput = {
      organization_id: activeOrgId,
      name,
      description: createDescription.trim() || undefined,
      enabled: createEnabled,
      trigger_type: triggerType,
      trigger_conditions: {},
      actions: [{ type: actionType, params }],
    };

    setCreateSubmitting(true);
    try {
      const { error } = await automationsService.create(input);
      if (error) {
        toast.error('Failed to create automation');
        return;
      }

      toast.success('Automation created');
      setShowCreateModal(false);
      void fetchAutomations();
      void fetchLogs();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create automation');
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleDelete = async (automation: Automation) => {
    if (!activeOrgId) return;
    if (!window.confirm(`Delete automation "${automation.name}"?`)) return;

    const { error } = await automationsService.delete(automation.id, activeOrgId);
    
    if (error) {
      toast.error('Failed to delete automation');
    } else {
      toast.success('Automation deleted');
      void fetchAutomations();
    }
  };

  if (loading) {
    return <div className="rounded-xl bg-white p-12 text-center">Loading automations...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Automations</h2>
          <p className="text-sm text-gray-600 mt-1">
            Automate repetitive tasks and workflows
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
        >
          ➕ Create Automation
        </button>
      </div>

      {/* Automations List */}
      {automations.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12 text-center">
          <div className="text-4xl mb-4">⚡</div>
          <h3 className="text-lg font-semibold text-gray-900">No Automations Yet</h3>
          <p className="mt-2 text-sm text-gray-600">
            Create your first automation to streamline your workflow
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90"
          >
            Create Automation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {automations.map(automation => (
            <div
              key={automation.id}
              className="rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {automation.name}
                    </h3>
                    <span className={`rounded-full px-2 py-1 text-xs font-semibold ${
                      automation.enabled
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {automation.enabled ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {automation.description && (
                    <p className="mt-1 text-sm text-gray-600">
                      {automation.description}
                    </p>
                  )}
                  <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                    <span>📍 Trigger: {automation.trigger_type.replace(/_/g, ' ')}</span>
                    <span>🎯 Actions: {automation.actions.length}</span>
                    <span>▶️ Runs: {automation.run_count}</span>
                    {automation.last_run_at && (
                      <span>
                        Last run: {new Date(automation.last_run_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggle(automation)}
                    className={`rounded-lg px-3 py-1 text-sm font-medium ${
                      automation.enabled
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {automation.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => toast('Edit automation coming soon', { icon: 'ℹ️' })}
                    className="rounded-lg bg-blue-50 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-100"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(automation)}
                    className="rounded-lg bg-red-50 px-3 py-1 text-sm font-medium text-red-700 hover:bg-red-100"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal Placeholder */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-6">
            <h2 className="text-xl font-bold text-gray-900">Create Automation</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  placeholder="e.g. Auto-assign new tasks"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Description (optional)</label>
                <input
                  value={createDescription}
                  onChange={(e) => setCreateDescription(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  id="pm-automation-enabled"
                  type="checkbox"
                  checked={createEnabled}
                  onChange={(e) => setCreateEnabled(e.target.checked)}
                />
                <label htmlFor="pm-automation-enabled" className="text-sm text-gray-700">Enabled</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Trigger</label>
                <select
                  value={createTriggerType}
                  onChange={(e) => setCreateTriggerType(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="task_created">Task created</option>
                  <option value="task_updated">Task updated</option>
                  <option value="task_status_changed">Task status changed</option>
                  <option value="task_assigned">Task assigned</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Action</label>
                <select
                  value={createActionType}
                  onChange={(e) => setCreateActionType(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="add_comment">Add comment</option>
                  <option value="set_task_status">Set task status</option>
                  <option value="assign_task">Assign task</option>
                  <option value="update_field">Update field</option>
                </select>
              </div>

              {createActionType === 'add_comment' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Comment message</label>
                  <textarea
                    value={actionMessage}
                    onChange={(e) => setActionMessage(e.target.value)}
                    rows={4}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  />
                </div>
              )}

              {createActionType === 'set_task_status' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <select
                    value={actionStatus}
                    onChange={(e) => setActionStatus(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="backlog">Backlog</option>
                    <option value="todo">To Do</option>
                    <option value="in-progress">In Progress</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              )}

              {createActionType === 'assign_task' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Assignee</label>
                  <select
                    value={actionAssigneeId}
                    onChange={(e) => setActionAssigneeId(e.target.value)}
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Select assignee</option>
                    {members.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {getDisplayName(m)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {createActionType === 'update_field' && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Field</label>
                    <input
                      value={actionField}
                      onChange={(e) => setActionField(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                      placeholder="e.g. priority"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Value</label>
                    <input
                      value={actionValue}
                      onChange={(e) => setActionValue(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              )}

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={createSubmitting}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  {createSubmitting ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-gray-200 bg-white p-5">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Execution Logs</h3>
            <p className="mt-1 text-sm text-gray-600">Recent automation runs</p>
          </div>
          <button
            onClick={() => void fetchLogs()}
            className="rounded-lg bg-gray-100 px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-200"
          >
            Refresh
          </button>
        </div>

        <div className="mb-4 flex items-center gap-3">
          <select
            value={selectedAutomationId}
            onChange={(e) => setSelectedAutomationId(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">All automations</option>
            {automations.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <div className="text-sm text-gray-600">
            {logs.length} entries
          </div>
        </div>

        {logsLoading ? (
          <div className="text-sm text-gray-500">Loading logs...</div>
        ) : logs.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-8 text-center">
            <div className="text-3xl mb-2">📜</div>
            <p className="text-sm text-gray-600">No logs yet</p>
            <p className="mt-1 text-xs text-gray-500">Create tasks/comments/time entries to trigger automations</p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map(log => (
              <div key={log.id} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900">
                      {log.status === 'success' ? '✅' : log.status === 'partial' ? '⚠️' : '❌'} {log.status}
                    </div>
                    <div className="mt-1 text-xs text-gray-600">
                      Trigger: {log.triggered_by_entity_type} • {new Date(log.executed_at).toLocaleString()}
                      {typeof log.duration_ms === 'number' ? ` • ${log.duration_ms}ms` : ''}
                    </div>
                    {log.error_message ? (
                      <div className="mt-1 text-xs text-red-600">{log.error_message}</div>
                    ) : null}
                  </div>
                  <div className="text-xs text-gray-400">
                    {log.automation_id ? `${log.automation_id.slice(0, 8)}…` : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AutomationsTab;
